import helper from 'store/helper';
import { PRIORITY_AXIS_MEDIA_SYNC } from 'domain/consts';
import types from './types';

// Report modes
export const MODE_NORMAL = 'normal';
export const MODE_PIVOT = 'pivot';
export const MODE_CV_ATTRIBUTES = 'cv_attributes';

// Product axis
export const PRODUCT_AXIS_AD = 'ad_product';
export const PRODUCT_AXIS_PURCHASED = 'purchased_product';


const FIRST_PAGE = 1;
const EMPTY_HIDDEN_METRICS = [];

export const initialState = {
  // Report settings
  settings: {
    mode: MODE_NORMAL,
    rowDimensions: ['media'],
    columnDimensions: [],
    metrics: ['cv_total', 'total_revenue', 'roas'],
    axis: PRIORITY_AXIS_MEDIA_SYNC,
    productAxis: PRODUCT_AXIS_AD,
    filters: {},
    // Touch point targets for CV attributes filter (接触タイミング)
    // When filters are active, only CVs with matching attribution_no are selected.
    touchPointTargets: [
      'direct', 'indirect2', 'indirect3', 'indirect4', 'indirect5',
      'indirect6', 'indirect7', 'indirect8', 'indirect9', 'indirect10',
      'first_touch',
    ],
    cvDisplayColumns: {
      cv_attributes: ['cv_name', 'cv_time', 'user_id', 'user_name', 'amount', 'shopify_amount', 'purchased_product', 'product_category', 'is_new', 'other1', 'other2', 'other3', 'other4', 'other5', 'latency', 'touch_count'],
      touch_history: ['direct', 'indirect2', 'indirect3', 'indirect4', 'indirect5', 'indirect6', 'indirect7', 'indirect8', 'indirect9', 'indirect10', 'first_touch'],
      touch_display: ['time', 'channel_type', 'category', 'ad_product', 'ad_product_category'],
    },
  },
  // Table state
  table: {
    data: {
      list: [],
      sum: {},
      pivotColumns: [],
    },
    settings: {
      sort: {
        field: 'cv_total',
        direction: 'DESC',
      },
      pagination: {
        currentPage: FIRST_PAGE,
        totalItems: 0,
        itemsPerPage: 50,
      },
    },
  },
  // Communication status
  status: 'idle',
  fetchedMode: null,
  fetchedSettings: null,
  fetchedMeta: null,
  error: null,
};

const requestStart = (state) => ({
  ...state,
  status: 'loading',
  error: null,
});

const requestFailed = (state, action) => ({
  ...state,
  status: 'failed',
  error: action.payload.error,
});

const fetchReportSucceeded = (state, action) => {
  const { data, metadata } = action.payload;
  return {
    ...state,
    table: {
      ...state.table,
      data: {
        list: data || [],
        sum: metadata?.sum || {},
        pivotColumns: metadata?.pivot_columns || [],
      },
      settings: {
        ...state.table.settings,
        pagination: {
          ...state.table.settings.pagination,
          totalItems: metadata?.total_count || 0,
        },
      },
    },
    // Track which mode the data was fetched in so we render the right table
    fetchedMode: state.settings.mode,
    // Snapshot settings at fetch time (table uses these, not live settings)
    fetchedSettings: {
      rowDimensions: state.settings.rowDimensions,
      columnDimensions: state.settings.columnDimensions,
      metrics: state.settings.metrics,
      axis: state.settings.axis,
      productAxis: state.settings.productAxis,
    },
    fetchedMeta: {
      hiddenMetrics: metadata?.hidden_metrics ?? EMPTY_HIDDEN_METRICS,
    },
    status: 'succeeded',
    error: null,
  };
};

const setMode = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    mode: action.payload.mode,
  },
  // Clear table data when switching modes (don't carry over between modes)
  table: {
    ...state.table,
    data: { list: [], sum: {}, pivotColumns: [] },
  },
  status: 'idle',
  fetchedMode: null,
  fetchedSettings: null,
  fetchedMeta: null,
});

const setRowDimensions = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    rowDimensions: action.payload.dimensions,
  },
});

const setColumnDimensions = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    columnDimensions: action.payload.dimensions,
  },
});

const setMetrics = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    metrics: action.payload.metrics,
  },
});

const setAxis = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    axis: action.payload.axis,
  },
});

const setProductAxis = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    productAxis: action.payload.productAxis,
  },
});


const setFilters = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    filters: action.payload.filters,
  },
});

const setTouchPointTargets = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    touchPointTargets: action.payload.targets,
  },
});

const setCvDisplayColumns = (state, action) => ({
  ...state,
  settings: {
    ...state.settings,
    cvDisplayColumns: {
      ...state.settings.cvDisplayColumns,
      [action.payload.section]: action.payload.columns,
    },
  },
});

const updatePage = (state, action) => ({
  ...state,
  table: {
    ...state.table,
    settings: {
      ...state.table.settings,
      pagination: {
        ...state.table.settings.pagination,
        currentPage: action.payload.page,
      },
    },
  },
});

const updateSort = (state, action) => ({
  ...state,
  table: {
    ...state.table,
    settings: {
      ...state.table.settings,
      sort: {
        field: action.payload.field,
        direction: action.payload.direction,
      },
      pagination: {
        ...state.table.settings.pagination,
        currentPage: FIRST_PAGE,
      },
    },
  },
});

const resetSettings = () => ({
  ...initialState,
});

const hydrateSettings = (state, action) => {
  const {
    mode,
    rowDimensions,
    columnDimensions,
    metrics,
    axis,
    productAxis,
    cvDisplayColumns,
  } = action.payload;
  return {
    ...state,
    settings: {
      ...state.settings,
      ...(mode !== undefined ? { mode } : {}),
      ...(rowDimensions !== undefined ? { rowDimensions } : {}),
      ...(columnDimensions !== undefined ? { columnDimensions } : {}),
      ...(metrics !== undefined ? { metrics } : {}),
      ...(axis !== undefined ? { axis } : {}),
      ...(productAxis !== undefined ? { productAxis } : {}),
      ...(cvDisplayColumns !== undefined ? { cvDisplayColumns } : {}),
    },
  };
};

const AdebifyCustomReportReducer = helper.createReducer(initialState, {
  [types.REQUEST_START]: requestStart,
  [types.REQUEST_FAILED]: requestFailed,
  [types.FETCH_REPORT_SUCCEEDED]: fetchReportSucceeded,
  [types.SET_MODE]: setMode,
  [types.SET_ROW_DIMENSIONS]: setRowDimensions,
  [types.SET_COLUMN_DIMENSIONS]: setColumnDimensions,
  [types.SET_METRICS]: setMetrics,
  [types.SET_AXIS]: setAxis,
  [types.SET_PRODUCT_AXIS]: setProductAxis,
  [types.SET_FILTERS]: setFilters,
  [types.SET_TOUCH_POINT_TARGETS]: setTouchPointTargets,
  [types.SET_CV_DISPLAY_COLUMNS]: setCvDisplayColumns,
  [types.UPDATE_PAGE]: updatePage,
  [types.UPDATE_SORT]: updateSort,
  [types.RESET_SETTINGS]: resetSettings,
  [types.HYDRATE_SETTINGS]: hydrateSettings,
});

export default AdebifyCustomReportReducer;
